import { useNavigate, useParams } from 'react-router-dom';
import { normalizeKindId } from '@gm-binder/shared';
import type { OverviewBlock } from '@gm-binder/shared';
import { PageHeader, Card, Button, EmptyState, Textarea, Field, Input } from '../../components/ui';
import { GenericList } from '../../components/views/Generic';
import { ImageInput } from '../../components/views/EntityForm';
import { CompendiumBrowser } from '../../components/views/CompendiumBrowser';
import { MusicManager } from '../../components/views/MusicManager';
import { useEditorStore } from '../../store/editorStore';
import { useUiStore } from '../../store/uiStore';
import { addEntity, listEntitiesOf, nextEntityId } from '../../lib/entityBuckets';
import { srdToLocal } from '../../lib/srdImport';
import { resolveKindDef, roleField } from '../../lib/viewRegistry';
import { overviewSections, newOverviewBlock } from '../../lib/overview';
import styles from './EntityPages.module.css';

const BESPOKE_PLACEHOLDER: Record<string, string> = {
  initiative: "L'iniziativa si gestisce durante la sessione nel binder.",
};

/** A plain markdown field editor used by singleton editors (notes/page). No inline preview. */
function MarkdownField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} className={styles.full}>
      <Textarea rows={6} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

/** Dynamic, GM-authored overview sections (custom titles + markdown body, reorderable). */
function OverviewEditor() {
  const adventure = useEditorStore((s) => s.adventure)!;
  const update = useEditorStore((s) => s.update);
  const sections = overviewSections(adventure.overview);

  const mutate = (fn: (list: OverviewBlock[]) => OverviewBlock[]) =>
    update((a) => {
      const current = Array.isArray(a.overview.sections)
        ? a.overview.sections
        : overviewSections(a.overview);
      a.overview = { ...a.overview, sections: fn(current.map((b) => ({ ...b }))) };
    });

  const setField = (id: string, key: 'title' | 'body' | 'icon', value: string) =>
    mutate((l) => l.map((b) => (b.id === id ? { ...b, [key]: value } : b)));
  const remove = (id: string) => mutate((l) => l.filter((b) => b.id !== id));
  const move = (id: string, dir: -1 | 1) =>
    mutate((l) => {
      const i = l.findIndex((b) => b.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= l.length) return l;
      const copy = [...l];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  return (
    <div className={styles.ovList}>
      {sections.length === 0 && (
        <p className={styles.muted}>
          Nessuna sezione. Aggiungi blocchi liberi (prologo, agganci, regole della casa…) con i
          titoli che preferisci.
        </p>
      )}
      {sections.map((b, i) => (
        <Card key={b.id} className={styles.ovSection}>
          <div className={styles.ovHead}>
            <Input
              className={styles.ovIcon}
              value={b.icon ?? ''}
              maxLength={4}
              aria-label="Icona"
              onChange={(e) => setField(b.id, 'icon', e.target.value)}
            />
            <Input
              className={styles.ovTitle}
              value={b.title}
              placeholder="Titolo sezione"
              aria-label="Titolo"
              onChange={(e) => setField(b.id, 'title', e.target.value)}
            />
            <div className={styles.ovActions}>
              <Button
                variant="ghost"
                icon="↑"
                aria-label="Sposta su"
                disabled={i === 0}
                onClick={() => move(b.id, -1)}
              />
              <Button
                variant="ghost"
                icon="↓"
                aria-label="Sposta giù"
                disabled={i === sections.length - 1}
                onClick={() => move(b.id, 1)}
              />
              <Button
                variant="ghost"
                icon="🗑"
                aria-label="Rimuovi"
                onClick={() => remove(b.id)}
              />
            </div>
          </div>
          <Textarea
            rows={5}
            value={b.body}
            placeholder="Contenuto (markdown)…"
            onChange={(e) => setField(b.id, 'body', e.target.value)}
          />
        </Card>
      ))}
      <Button variant="default" icon="＋" onClick={() => mutate((l) => [...l, newOverviewBlock()])}>
        Aggiungi sezione
      </Button>
    </div>
  );
}

/** Map upload banner shown above the rooms list. */
function MapBanner({ slug }: { slug: string }) {
  const adventure = useEditorStore((s) => s.adventure)!;
  const update = useEditorStore((s) => s.update);
  const rooms = adventure.rooms ?? { floors: [], rooms: [] };
  const setMap = (v: string | undefined) =>
    update((a) => {
      a.rooms = { ...(a.rooms ?? { floors: [], rooms: [] }), mapImage: v };
    });
  const setCaption = (v: string) =>
    update((a) => {
      a.rooms = { ...(a.rooms ?? { floors: [], rooms: [] }), mapCaption: v || undefined };
    });
  return (
    <Card className={styles.mapBanner}>
      <div className={styles.blockLabel}>Mappa della sessione</div>
      <ImageInput slug={slug} label="Mappa" value={rooms.mapImage ?? ''} onChange={setMap} />
      <Field label="Didascalia mappa">
        <Input
          value={rooms.mapCaption ?? ''}
          placeholder="es. Pianta del maniero — piano terra"
          onChange={(e) => setCaption(e.target.value)}
        />
      </Field>
    </Card>
  );
}

export function EntityListPage() {
  const { slug = '', sectionId = '' } = useParams();
  const navigate = useNavigate();
  const adventure = useEditorStore((s) => s.adventure);
  const update = useEditorStore((s) => s.update);
  const toast = useUiStore((s) => s.toast);
  if (!adventure) return null;

  const section = adventure.sections.find((s) => s.id === sectionId);
  if (!section) return <EmptyState icon="📄" title="Sezione non trovata" />;

  const kindId = normalizeKindId(section.kind ?? section.type ?? 'custom');
  const kindDef = resolveKindDef(kindId, adventure.kinds as Parameters<typeof resolveKindDef>[1]);

  // --- Bespoke / play-time sections: friendly placeholder ---
  if (BESPOKE_PLACEHOLDER[kindId]) {
    return (
      <div className={styles.stack}>
        <PageHeader eyebrow="Sezione" title={section.title} />
        <Card>
          <p className={styles.muted}>{BESPOKE_PLACEHOLDER[kindId]}</p>
        </Card>
      </div>
    );
  }

  // --- Compendium: SRD browser with import into bestiary/items ---
  if (kindId === 'compendium') {
    const importEntry = (
      kind: 'beast' | 'item',
      detail: Record<string, unknown>,
      type: Parameters<typeof srdToLocal>[0],
    ) => {
      const local = srdToLocal(type, detail);
      const existing = listEntitiesOf(adventure, kind);
      const id = nextEntityId(existing, String(local.name ?? 'voce'));
      update((a) => addEntity(a, kind, { id, ...local }));
      toast(
        `"${String(local.name)}" importato in ${kind === 'beast' ? 'Bestiario' : 'Oggetti'}`,
        'success',
      );
    };
    return (
      <div className={styles.stack}>
        <PageHeader eyebrow="Sezione" title={section.title} subtitle="Riferimento SRD (D&D 5e API)" />
        <CompendiumBrowser
          onImport={importEntry}
          hint="I mostri vengono importati nel Bestiario; equipaggiamento e oggetti magici negli Oggetti."
        />
      </div>
    );
  }

  // --- Music: track management (upload / list / delete / preview) ---
  if (kindId === 'music') {
    return (
      <div className={styles.stack}>
        <PageHeader eyebrow="Sezione" title={section.title} subtitle="Colonna sonora dell'avventura" />
        <MusicManager slug={slug} />
      </div>
    );
  }

  // --- Notes singleton (adventure.authorNotes) ---
  if (kindId === 'notes') {
    return (
      <div className={styles.stack}>
        <PageHeader eyebrow="Sezione" title={section.title} />
        <Card>
          <MarkdownField
            label="Note del DM"
            value={adventure.authorNotes ?? ''}
            onChange={(v) => update((a) => void (a.authorNotes = v))}
          />
        </Card>
      </div>
    );
  }

  // --- Overview singleton (adventure.overview) — dynamic, GM-authored sections ---
  if (kindId === 'overview') {
    return (
      <div className={styles.stack}>
        <PageHeader
          eyebrow="Sezione"
          title={section.title}
          subtitle="Blocchi liberi con i titoli che preferisci: prologo, agganci, regole della casa…"
        />
        <OverviewEditor />
      </div>
    );
  }

  // --- Page singleton (a markdown page identified by section.pageId) ---
  if (section.pageId) {
    const pageId = section.pageId;
    const page = adventure.pages.find((p) => p.id === pageId);
    if (!page) return <EmptyState icon="📄" title="Pagina non trovata" />;
    const setPage = (patch: Record<string, unknown>) =>
      update((a) => {
        const p = a.pages.find((x) => x.id === pageId);
        if (p) Object.assign(p, patch);
      });
    return (
      <div className={styles.stack}>
        <PageHeader eyebrow="Pagina" title={section.title} />
        <Card>
          <div className={styles.metaGrid}>
            <Field label="Titolo">
              <Input value={page.title} onChange={(e) => setPage({ title: e.target.value })} />
            </Field>
            <Field label="Icona">
              <Input value={page.icon ?? ''} maxLength={4} onChange={(e) => setPage({ icon: e.target.value })} />
            </Field>
          </div>
          <MarkdownField
            label="Contenuto"
            value={page.markdown ?? ''}
            onChange={(v) => setPage({ markdown: v })}
          />
        </Card>
      </div>
    );
  }

  // --- List kinds (built-in buckets + custom) ---
  const entities = listEntitiesOf(adventure, kindId);
  const titleKey = roleField(kindDef, 'title')?.key ?? 'name';

  const addNew = () => {
    const base = `Nuovo ${kindDef.label}`;
    const id = nextEntityId(entities, base);
    update((a) => addEntity(a, kindId, { id, [titleKey]: base }));
    navigate(`/library/${slug}/edit/${sectionId}/${id}`);
  };

  return (
    <div className={styles.stack}>
      <PageHeader
        eyebrow="Sezione"
        title={section.title}
        subtitle={`${entities.length} ${entities.length === 1 ? 'voce' : 'voci'}`}
        actions={
          <Button variant="primary" icon="＋" onClick={addNew}>
            Aggiungi
          </Button>
        }
      />
      {kindId === 'place' && <MapBanner slug={slug} />}
      <GenericList
        kind={kindDef}
        entities={entities}
        slug={slug}
        onOpen={(e) => navigate(`/library/${slug}/edit/${sectionId}/${String(e.id)}`)}
      />
    </div>
  );
}
